import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackLead } from '@/utils/analytics';
import { getUtmParams } from '@/utils/utm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZONES = ['CABA', 'Zona Norte', 'Zona Sur', 'Zona Oeste'];

const NEIGHBORHOODS = {
  'CABA': [
    'Palermo', 'Recoleta', 'Belgrano', 'Caballito', 'Almagro', 'Villa Crespo',
    'Flores', 'Floresta', 'Boedo', 'San Telmo', 'Barracas', 'La Boca',
    'Puerto Madero', 'Parque Patricios', 'Villa Urquiza', 'Colegiales', 'Chacarita',
    'Villa Devoto', 'Villa del Parque', 'Monte Castro', 'Versalles', 'Liniers',
    'Mataderos', 'Saavedra', 'Nuñez', 'Agronomía', 'Villa Ortúzar', 'Parque Chas'
  ],
  'Zona Norte': [
    'Vicente López', 'Olivos', 'La Lucila', 'Florida', 'Martínez', 'Acassuso',
    'San Isidro', 'Beccar', 'Boulogne', 'Villa Adelina', 'Tigre', 'San Fernando',
    'Nordelta', 'General Pacheco', 'Don Torcuato', 'Campana', 'Escobar', 'Pilar'
  ],
  'Zona Oeste': [
    'Ramos Mejía', 'Haedo', 'Morón', 'Castelar', 'Ituzaingó', 'San Justo',
    'Villa Luzuriaga', 'Isidro Casanova', 'Laferrere', 'Merlo', 'Paso del Rey'
  ],
  'Zona Sur': [
    'Avellaneda', 'Lanús', 'Banfield', 'Lomas de Zamora', 'Temperley', 'Adrogué',
    'Burzaco', 'Quilmes', 'Bernal', 'Ezpeleta', 'Berazategui', 'Florencio Varela'
  ]
};

const CATEGORIES = [
  'Plomería', 'Electricidad', 'Gasista', 'Pintura', 'Cerrajería',
  'Aire acondicionado', 'Arreglos Generales', 'Jardinería', 'Techista'
];

// Categorías visuales principales (cards rápidas)
const QUICK_CATEGORIES = [
  { label: 'Plomero',           value: 'Plomería',          icon: '🔧' },
  { label: 'Electricista',      value: 'Electricidad',       icon: '⚡' },
  { label: 'Gasista',           value: 'Gasista',            icon: '🔥' },
  { label: 'Arreglos Generales',value: 'Arreglos Generales', icon: '🏠' },
];

export default function CreateTicket() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [ticketCreated, setTicketCreated] = useState(false);
  const [creationEnabled, setCreationEnabled] = useState(null); // null = cargando
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    zone: '',
    neighborhood: '',
    category: '',
    description: '',
    urgency: 'NORMAL',
    terms_accepted: false
  });

  // Verificar si la creación de tickets está habilitada al montar
  useEffect(() => {
    axios.get(`${API}/tickets/creation-status`)
      .then(r => setCreationEnabled(r.data.ticket_creation_enabled))
      .catch(() => setCreationEnabled(true)); // si falla, asumir habilitado
  }, []);

  // ── Validación y avance al paso 2 ─────────────────────────────────────
  const handleContinue = () => {
    if (!formData.category) {
      toast.error('Seleccioná el tipo de servicio que necesitás');
      return;
    }
    if (formData.description.trim().length < 10) {
      toast.error('Contanos más sobre el problema (mínimo 10 caracteres)');
      return;
    }
    if (!formData.zone) {
      toast.error('Seleccioná tu zona');
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Envío final (sin cambios de lógica) ──────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.terms_accepted) {
      toast.error('Debés aceptar los términos y condiciones');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/tickets`, { ...formData, ...getUtmParams() });
      trackLead();
      setTicketCreated(true);
    } catch (error) {
      if (error.response?.status === 503) {
        setCreationEnabled(false);
      } else {
        console.error('Error creating ticket:', error);
        toast.error(error.response?.data?.detail || 'Error al publicar el trabajo');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Selección de categoría por card ──────────────────────────────────
  const selectQuickCategory = (value) => {
    setFormData({ ...formData, category: value });
  };

  // ── Pantalla de confirmación (sin cambios) ───────────────────────────
  // Cargando estado de creación de tickets
  if (creationEnabled === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Creación de tickets deshabilitada (mantenimiento)
  if (!creationEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center" data-testid="ticket-creation-maintenance">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-gray-200">
          <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Mantenimiento</h2>
        <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
          La creación de solicitudes está en mantenimiento temporalmente, te avisaremos cuando puedas volver a usarla.
        </p>
        <button
          onClick={() => navigate('/')}
          className="mt-6 text-sm text-gray-400 hover:text-gray-600 underline"
        >
          Volver a la página de inicio
        </button>
      </div>
    );
  }

  if (ticketCreated) {    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="confirmation-banner">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border border-gray-200 p-8 sm:p-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 rounded-full bg-green-50">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
            ¡Tu solicitud fue publicada correctamente!
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            En unos instantes recibirás un email con un enlace para gestionar tu solicitud.
          </p>
          <div className="text-left bg-gray-50 rounded-xl p-5 mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Desde ese enlace podrás:</p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-gray-400">•</span>
                Cancelar el ticket si ya no necesitás el servicio
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-gray-400">•</span>
                Marcar el trabajo como concretado
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-500 mb-8 leading-relaxed border-t pt-5">
            Esto es importante para que los proveedores dejen de ver tu solicitud y no sigan contactándote.
          </p>
          <Button
            data-testid="back-to-home-btn"
            onClick={() => navigate('/')}
            className="w-full text-base py-5"
            style={{ backgroundColor: 'var(--primary-strong)' }}
          >
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="create-ticket-page">
      {/* Header */}
      <header className="bg-white px-4 py-4 border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => step === 1 ? navigate('/') : setStep(1)}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--primary-strong)' }}>
            Solicitar un arreglo
          </h1>
        </div>
      </header>

      {/* Indicador de progreso */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Paso {step} de 2
            </span>
            <span className="text-xs text-gray-400">
              {step === 1 ? 'Información del trabajo' : 'Datos de contacto'}
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: step === 1 ? '50%' : '100%',
                backgroundColor: 'var(--primary-strong)'
              }}
            />
          </div>
        </div>
      </div>

      {/* Contenido */}
      <main className="max-w-xl mx-auto px-4 py-6 pb-24 sm:pb-6">

        {/* ══════════════ PASO 1 ══════════════ */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Categoría — cards rápidas */}
            <div>
              <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--dark-primary)' }}>
                ¿Qué necesitás arreglar hoy?
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Seleccioná el tipo de servicio que buscás
              </p>

              <div className="grid grid-cols-2 gap-3" data-testid="category-cards">
                {QUICK_CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    data-testid={`category-card-${cat.value}`}
                    onClick={() => selectQuickCategory(cat.value)}
                    className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 p-4 min-h-[88px] text-sm font-semibold transition-all active:scale-95 ${
                      formData.category === cat.value
                        ? 'border-orange-400 bg-orange-50 text-orange-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl leading-none">{cat.icon}</span>
                    <span className="text-center leading-snug">{cat.label}</span>
                  </button>
                ))}
              </div>

              {/* Selector siempre visible para otras categorías */}
              <div className="mt-3">
                <Select
                  value={QUICK_CATEGORIES.some(c => c.value === formData.category) ? '' : formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="category-select" className="bg-white text-gray-500">
                    <SelectValue placeholder="¿No encontrás tu servicio? Seleccioná otro" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-base font-semibold" style={{ color: 'var(--dark-primary)' }}>
                Contanos qué necesitás arreglar
              </Label>
              <p className="text-sm text-gray-500 -mt-1">
                Mientras más detalles incluyas, más fácil será encontrar al profesional adecuado.
              </p>
              <Textarea
                id="description"
                data-testid="description-input"
                placeholder="Tengo una pérdida de agua debajo de la bacha de la cocina y necesito que la revisen esta semana."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                minLength={10}
                maxLength={1000}
                rows={4}
                className="resize-none bg-white text-base"
              />
              <p className="text-xs text-gray-400">{formData.description.length}/1000</p>
            </div>

            {/* Urgencia */}
            <div className="space-y-3">
              <Label className="text-base font-semibold" style={{ color: 'var(--dark-primary)' }}>
                ¿Necesitás resolverlo con urgencia?
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="urgency-urgente-btn"
                  onClick={() => setFormData({ ...formData, urgency: 'URGENTE' })}
                  className={`rounded-xl border-2 p-4 text-sm font-medium transition-all text-left active:scale-95 ${
                    formData.urgency === 'URGENTE'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span className="block text-base mb-1">🚨 Sí, es urgente</span>
                  <span className="text-xs font-normal opacity-75">Necesito una solución pronto</span>
                </button>
                <button
                  type="button"
                  data-testid="urgency-normal-btn"
                  onClick={() => setFormData({ ...formData, urgency: 'NORMAL' })}
                  className={`rounded-xl border-2 p-4 text-sm font-medium transition-all text-left active:scale-95 ${
                    formData.urgency === 'NORMAL'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span className="block text-base mb-1">Puede esperar</span>
                  <span className="text-xs font-normal opacity-75">No hay apuro</span>
                </button>
              </div>
            </div>

            {/* Zona */}
            <div className="space-y-2">
              <Label htmlFor="zone" className="text-base font-semibold" style={{ color: 'var(--dark-primary)' }}>
                ¿En qué zona estás? *
              </Label>
              <Select
                value={formData.zone}
                onValueChange={(value) => setFormData({ ...formData, zone: value, neighborhood: '' })}
              >
                <SelectTrigger data-testid="zone-select" className="bg-white h-12 text-base">
                  <SelectValue placeholder="Seleccioná tu zona" />
                </SelectTrigger>
                <SelectContent>
                  {ZONES.map((zone) => (
                    <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Botón continuar */}
            <Button
              type="button"
              data-testid="continue-btn"
              disabled
              onClick={handleContinue}
              className="w-full text-base py-6 mt-2 cursor-not-allowed opacity-60"
              style={{ backgroundColor: 'var(--primary-strong)' }}
            >
              Continuar →
            </Button>
          </div>
        )}

        {/* ══════════════ PASO 2 ══════════════ */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="mb-2">
              <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--dark-primary)' }}>
                ¿A quién te contactamos?
              </h2>
              <p className="text-sm text-gray-500">
                Completá tus datos para que los profesionales puedan comunicarse con vos.
              </p>
            </div>

            {/* Nombre */}
            <div className="space-y-1.5">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                data-testid="name-input"
                placeholder="Tu nombre"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                minLength={2}
                maxLength={100}
                className="h-12 text-base bg-white"
              />
            </div>

            {/* Teléfono */}
            <div className="space-y-1.5">
              <Label htmlFor="phone">Teléfono *</Label>
              <Input
                id="phone"
                data-testid="phone-input"
                type="tel"
                inputMode="tel"
                placeholder="11 1234-5678"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                minLength={8}
                maxLength={20}
                className="h-12 text-base bg-white"
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                inputMode="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12 text-base bg-white"
              />
            </div>

            {/* Barrio o Localidad */}
            <div className="space-y-1.5">
              <Label htmlFor="neighborhood">
                Barrio o Localidad{' '}
                <span className="text-gray-400 font-normal text-xs">(opcional)</span>
              </Label>
              <Select
                value={formData.neighborhood}
                onValueChange={(value) => setFormData({ ...formData, neighborhood: value })}
                disabled={!formData.zone}
              >
                <SelectTrigger data-testid="neighborhood-select" className="bg-white h-12 text-base" disabled={!formData.zone}>
                  <SelectValue placeholder={formData.zone ? 'Seleccioná tu barrio o localidad' : 'Primero seleccioná una zona'} />
                </SelectTrigger>
                <SelectContent>
                  {formData.zone && NEIGHBORHOODS[formData.zone]?.map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Términos */}
            <div className="flex items-start space-x-2 pt-1">
              <Checkbox
                id="terms"
                data-testid="terms-checkbox"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => setFormData({ ...formData, terms_accepted: checked })}
              />
              <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                Acepto los términos y condiciones. Mi información será compartida con profesionales interesados.
              </Label>
            </div>

            {/* Submit */}
            <Button
              data-testid="submit-ticket-btn"
              type="submit"
              className="w-full text-base py-6 mt-2"
              style={{ backgroundColor: 'var(--primary-strong)' }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Publicando...
                </>
              ) : (
                'Publicar trabajo'
              )}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
